/* eslint-disable complexity */
import cx from "classnames";
/* eslint-disable max-lines */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { DeleteButton, ViewButton } from "../";
import { GameConceptIdSearchParam } from "../../helpers/dataFetchingHelpers";
import { GameConceptApplication } from "../../helpers/gameConceptHelpers";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { CrmActions } from "../../redux/actions/crmApiActions";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./AaaGameConceptsList.module.css";

type GameConceptRowProps = Readonly<{
  programDashboardRouteName: RouteNames;
  gameConcept: GameConceptApplication;
  index: number;
  className?: string;
  key: string | number;
  onDelete?: () => void;
}>;

export const GameConceptRow = ({
  gameConcept,
  programDashboardRouteName,
  index,
  className,
  key,
  onDelete,
}: GameConceptRowProps) => {
  const [
    untitledGame,
    draft,
    underReview,
    additionalContractsSent,
    rejected,
    approved,
    continueApplication,
    needMoreInformation,
    additionalReviewRequired,
  ] = useStrings(
    "untitledGame",
    "draft",
    "underReview",
    "additionalContractsSent",
    "rejected",
    "approved",
    "continueApplication",
    "needMoreInformation",
    "additionalReviewRequired"
  );
  const history = useHistory();
  const dispatch = useDispatch();

  const gameConceptInfo = gameConcept.gameConceptInfo;
  const gameState = gameConceptInfo.state;
  const gameStatus = gameState.status;
  const gameTitle = gameConceptInfo.details.gameTitle;
  const [isDeleting, setIsDeleting] = useState(false);

  let statusMessage = underReview;
  switch (gameStatus) {
    case CrmGameConceptStatus.Unset:
      statusMessage = draft;
      break;
    case CrmGameConceptStatus.UnderReview:
      statusMessage = underReview;
      break;
    case CrmGameConceptStatus.AdditionalContractsSent:
      statusMessage = additionalContractsSent;
      break;
    case CrmGameConceptStatus.Accepted:
      statusMessage = approved;
      break;
    case CrmGameConceptStatus.Rejected:
      statusMessage = rejected;
      break;
    case CrmGameConceptStatus.MoreInfoNeeded:
      statusMessage = needMoreInformation;
      break;
    case CrmGameConceptStatus.OnHold:
      statusMessage = additionalReviewRequired;
      break;
  }

  const viewOnlyGame =
    gameStatus === CrmGameConceptStatus.UnderReview ||
    gameStatus === CrmGameConceptStatus.AdditionalContractsSent ||
    gameStatus === CrmGameConceptStatus.OnHold ||
    gameStatus === CrmGameConceptStatus.Accepted ||
    gameStatus === CrmGameConceptStatus.Rejected;

  const continueButtonOnClick = () => {
    logPageAction(OnboardingPageAction.ContinueApplicationButton);
    // TODO: Investigate how to make this work client-side without breaking Fast-Track
    // and other edge cases
    history.push(getDashboardUrl(programDashboardRouteName, gameState.id));
  };

  const deleteButtonOnClick = () => {
    logPageAction(OnboardingPageAction.DeleteConceptButton);
    setIsDeleting(true);
    dispatch({
      type: CrmActions.DELETE_GAME_CONCEPT_START,
      program: Programs.Aaa,
      gameConceptId: gameConcept.id,
    });
    if (onDelete) {
      onDelete();
    }
  };

  const viewApplicationLinkOnClick = () => {
    logPageAction(OnboardingPageAction.ViewGameConceptLink);
    history.push({
      pathname: OnboardingUrls[RouteNames.AaaFormPreview],
      search: `?${GameConceptIdSearchParam}=${gameConcept.id}`,
    });
  };

  return (
    <div
      key={key}
      className={cx(commonStyles.rowContainer, index === 0 ? styles.firstGameRow : "", className)}
    >
      <div className={commonStyles.titleStatusContainer}>
        <h3 className={commonStyles.gameTitle}>{gameTitle !== "" ? gameTitle : untitledGame}</h3>
        <div className={styles.applicationState}>{statusMessage}</div>
      </div>
      {isDeleting && <XboxSpinner size={40} />}
      {!isDeleting && (
        <>
          {!viewOnlyGame && (
            <>
              <BasicButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={styles.navigationButton}
                onClick={continueButtonOnClick}
                role="button"
                textDecorationUnderline={true}
              >
                {gameStatus === CrmGameConceptStatus.MoreInfoNeeded
                  ? needMoreInformation
                  : continueApplication}
              </BasicButton>
              {gameStatus === CrmGameConceptStatus.Unset && (
                <DeleteButton onClick={deleteButtonOnClick} />
              )}
            </>
          )}
          {viewOnlyGame && <ViewButton onClick={viewApplicationLinkOnClick} />}
        </>
      )}
    </div>
  );
};
